import { ChevronLeft, ChevronRight } from 'lucide-react'
import { Button } from '../controls/Button'
import s from './GridPager.module.css'

export interface GridPagerProps {
  /** Zero-based offset of the rows currently rendered by the host. */
  offset: number
  /** Maximum rows requested for one page. */
  limit: number
  /** Number of rows actually rendered on this page. */
  loaded: number
  /** Exact matching total when the host knows it. */
  total: number | null
  /** Lowercase plural, e.g. "opportunities". */
  itemsLabel: string
  /** Prevents double navigation while the host is replacing the page. */
  busy?: boolean
  onPageChange: (offset: number) => void
}

/**
 * Paging chrome for a shared DataGrid footer: the range on the leading edge,
 * two compact ‹ › controls on the trailing one (#2375). The buttons are named
 * ("Next page of opportunities"); the glyph is what a person sees.
 *
 * This component owns no rows and performs no request: like DataGrid, it is the
 * control half of the contract. The host owns the governed query and hands the
 * next offset to its data adapter. That keeps console and portal pagination
 * identical without teaching the design system about either application's API.
 */
export function GridPager({
  offset, limit, loaded, total, itemsLabel, busy = false, onPageChange,
}: GridPagerProps) {
  const safeOffset = Math.max(0, offset)
  const safeLimit = Math.max(1, limit)
  const previous = Math.max(0, safeOffset - safeLimit)
  const next = safeOffset + safeLimit
  const canPrevious = safeOffset > 0
  const canNext = total == null ? loaded >= safeLimit : safeOffset + loaded < total
  const previousUnavailable = !canPrevious || busy
  const nextUnavailable = !canNext || busy
  const first = loaded > 0 ? safeOffset + 1 : 0
  const last = safeOffset + loaded
  const status = loaded === 0
    ? (safeOffset === 0 ? `0 ${itemsLabel}` : `No ${itemsLabel} on this page`)
    : `${first}–${last}${total == null ? '' : ` of ${total}`} ${itemsLabel}`

  return (
    <nav className={s.pager} aria-label={`Pagination for ${itemsLabel}`} aria-busy={busy || undefined}>
      <span className={s.status} role="status" aria-live="polite" aria-atomic="true">
        {status}
      </span>
      <span className={s.actions}>
        <Button type="button" size="sm" variant="ghost" icon={<ChevronLeft aria-hidden="true" />}
          aria-label={`Previous page of ${itemsLabel}`}
          aria-disabled={previousUnavailable}
          onClick={() => { if (!previousUnavailable) onPageChange(previous) }} />
        <Button type="button" size="sm" variant="ghost" icon={<ChevronRight aria-hidden="true" />}
          aria-label={`Next page of ${itemsLabel}`}
          aria-disabled={nextUnavailable}
          onClick={() => { if (!nextUnavailable) onPageChange(next) }} />
      </span>
    </nav>
  )
}
